@use '@/styles/tokens' as *;

.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: var(--text-secondary);
}

/* ── Header ───────────────────────────────────────────── */
/* Full-bleed gradient banner: negate the PageContainer padding (--space-6) so it
   spans to the white content-card edges, with matching 16px rounded top corners. */
.header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  margin: calc(var(--space-6) * -1) calc(var(--space-6) * -1) 0;
  padding: var(--space-5) var(--space-6);
  border-radius: 16px 16px 0 0;
  background-color: var(--surface-card);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.backCaret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-primary);
}

.identityCol {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.identity {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.customerName {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: var(--text-primary);
}

.orderId {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-secondary);
}

.contacts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
}

.contact {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 13px;
  color: var(--text-secondary);
}

.actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.statusTrigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 14px;
  background: var(--surface-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.statusTrigger:disabled {
  cursor: default;
  color: var(--text-tertiary);
  background: var(--surface-muted);
}

.statusFinal {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.finalLabel {
  font-size: 12px;
  color: var(--text-tertiary);
}

.editableValue {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.editIcon {
  display: inline-flex;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-tertiary);
}

.editIcon:hover {
  color: var(--brand-bg);
}

/* ── Body grid ────────────────────────────────────────── */
.body {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: var(--space-4);

  @include down(lg) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/* ── Cards ────────────────────────────────────────────── */
.card {
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
}

.cardTitle {
  margin: 0 0 var(--space-4);
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

/* ── Line items ───────────────────────────────────────── */
.lineItems {
  display: flex;
  flex-direction: column;
}

.lineItem {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--border-subtle);
}

.thumb {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-md);
  background: var(--surface-sunken);
  object-fit: cover;
}

.lineMain {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.lineName {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.lineLinks {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.placeholderLink {
  font-size: 13px;
  font-weight: 500;
  color: var(--brand-bg);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.placeholderLink:disabled {
  cursor: default;
}

.configured {
  font-size: 13px;
  color: var(--status-success);
  font-weight: 500;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 12px;
  color: var(--text-secondary);
}

.bundleDevices {
  margin: var(--space-1) 0 0;
  padding-left: var(--space-4);
  font-size: 12px;
  color: var(--text-tertiary);
}

.linePrice {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
  text-align: right;
}

.price {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.qty,
.installFee {
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ── Totals ───────────────────────────────────────────── */
.totals {
  margin: var(--space-4) 0 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.totals > div {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: var(--text-secondary);
}

.totals dt,
.totals dd {
  margin: 0;
}

.totalRow {
  margin-top: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-default);
}

.totalRow dt,
.totalRow dd {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

/* ── Meta / logistics rows ────────────────────────────── */
.metaList {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.metaRow {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.metaRow dt {
  margin: 0;
  font-size: 13px;
  color: var(--text-tertiary);
}

.metaRow dd {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  text-align: right;
  max-width: 60%;
}

/* ── Reviews drawer ───────────────────────────────────── */
.reviewList {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.reviewItem {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}

.reviewHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.stars {
  margin-left: auto;
  display: inline-flex;
  gap: 2px;
  color: #f5a623;
}

.reviewComment {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.empty {
  color: var(--text-tertiary);
  font-size: 13px;
}

/* ── Add Parameters modal ─────────────────────────────── */
.modalHelp {
  margin: 0 0 var(--space-4);
  font-size: 13px;
  color: var(--text-secondary);
}

.deviceCounter {
  margin: 0 0 var(--space-2);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary);
  text-align: right;
}

.deviceName {
  margin: 0 0 var(--space-3);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.modalField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-bottom: var(--space-4);
  font-size: 13px;
  color: var(--text-secondary);
}
